import React, { useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

/**
 * Deep-link entry for a CLASS INVITE (the link a teacher copies from the join-code
 * modal and sends to their class): /c/<classId>
 *
 * Same shape as AssignmentDeepLink (/a/…) and the material link (/m/…), but it lands
 * on the class hub rather than one piece of work — it's the join code as a link, so
 * a student who has never signed in can go from the link to inside the class in one
 * hop instead of "make an account, find the Hub, type six characters".
 *
 * Flow:
 *   - signed out       -> a clean, class-specific "Continue with Google" screen.
 *                         New accounts created from here are students (these links
 *                         are sent to students), then they land in the class.
 *   - student/nickname -> student portal opened on this class; the portal offers to
 *                         join if they aren't enrolled yet (?invite=1 is what tells
 *                         it this is a join link and not ordinary navigation).
 *   - teacher/admin    -> the app. A teacher clicking their own invite link should
 *                         not be dropped into the student portal.
 */
export const ClassInviteDeepLink: React.FC = () => {
  const { classId } = useParams();
  const { currentUser, userProfile, loading, startGoogleLogin, completeGoogleLogin } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-gray-500">Loading…</div>;
  }
  if (!classId) return <Navigate to="/" replace />;

  if (currentUser) {
    // Consume the redirect fallback — otherwise it lingers per-tab and bounces the
    // next visit to /login straight back here.
    if (sessionStorage.getItem('deepLink')) sessionStorage.removeItem('deepLink');
    if (!userProfile && !currentUser.isAnonymous) {
      return <div className="min-h-screen flex items-center justify-center text-gray-500">Loading…</div>;
    }
    if (userProfile?.role === 'student' || currentUser.isAnonymous) {
      return <Navigate to={`/student?openClass=${encodeURIComponent(classId)}&invite=1`} replace />;
    }
    return <Navigate to="/app" replace />;
  }

  const handleGoogle = async () => {
    setBusy(true);
    setError(null);
    // Fallback target in case the provider uses a full-page redirect flow.
    sessionStorage.setItem('deepLink', `/c/${classId}`);
    try {
      const { needsRole } = await startGoogleLogin();
      // Brand-new accounts arriving via a class invite are students.
      if (needsRole) await completeGoogleLogin('student');
    } catch {
      setError("Sign-in didn't complete. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="bg-white rounded-2xl shadow-lg max-w-sm w-full p-8 text-center">
        <div className="w-12 h-12 mx-auto rounded-xl bg-gradient-to-br from-[#59A8DA] to-[#5FA660] flex items-center justify-center text-white font-black text-lg mb-4">
          LUC
        </div>
        <h1 className="text-xl font-bold text-gray-900 mb-2">Join your class</h1>
        <p className="text-sm text-gray-500 mb-6">
          Sign in with your school Google account to join this class in Level Up Classroom.
        </p>
        <button
          onClick={handleGoogle}
          disabled={busy}
          className="w-full flex items-center justify-center gap-2.5 px-4 py-3 border border-gray-200 rounded-xl font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" />
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
            <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z" />
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
          </svg>
          {busy ? 'Signing in…' : 'Continue with Google'}
        </button>
        {error && <p className="text-sm text-red-600 mt-3">{error}</p>}
        <p className="text-[11px] text-gray-400 mt-5">You'll go straight into the class after signing in.</p>
        {/* This screen creates a STUDENT account for anyone new, which is right for
            the audience but wrong for a teacher or parent who was forwarded the
            link — and the role can't be changed afterwards. Give them a way out
            before an account is made. */}
        <p className="text-[11px] text-gray-400 mt-3">
          Not a student?{' '}
          <a href="/login" className="font-semibold text-gray-500 underline hover:text-gray-700">
            Sign in here instead
          </a>
        </p>
      </div>
    </div>
  );
};
